import React from 'react';

interface ExecuteResultsSectionProps {
  isLoading: boolean;
  onExecute: () => void;
  result: any;
  transformedRequest?: any;
}

export function ExecuteResultsSection({
  isLoading,
  onExecute,
  result,
  transformedRequest
}: ExecuteResultsSectionProps) {
  const hasResult = result && (result.label || result.error);
  const isSuccess = result && result.label && !result.error;

  return (
    <div className="space-y-6">
      {/* Execute Button */}
      <button
        onClick={onExecute}
        disabled={isLoading}
        className="w-full px-6 py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <>
            <span className="animate-spin inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
            Running Classification Pipeline...
          </>
        ) : (
          <>
            <span className="text-xl">🚀</span>
            Run Classification Pipeline
          </>
        )}
      </button>

      {/* Results Section */}
      {hasResult && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          {/* Results Header */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              {isSuccess ? (
                <>
                  <span className="text-2xl">✅</span>
                  Classification Result
                </>
              ) : (
                <>
                  <span className="text-2xl">❌</span>
                  Error
                </>
              )}
            </h3>
          </div>

          <div className="p-6 space-y-4">
            {/* Primary Result */}
            {isSuccess && (
              <div className="bg-green-50 dark:bg-green-900 dark:bg-opacity-30 border border-green-200 dark:border-green-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">🎯</span>
                  <div>
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">Classification</p>
                    <p className="text-lg font-bold text-green-900 dark:text-green-200">
                      {result.label}
                      {result.score !== null && (
                        <span className="text-sm font-normal ml-2 text-green-700 dark:text-green-300">
                          ({(result.score * 100).toFixed(1)}% confidence)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Display */}
            {result.error && (
              <div className="bg-red-50 dark:bg-red-900 dark:bg-opacity-30 border border-red-200 dark:border-red-700 rounded-lg p-4">
                <p className="text-sm text-red-800 dark:text-red-300 font-mono">{result.error}</p>
              </div>
            )}

            {/* Collapsible Sections */}
            <div className="space-y-3">
              {/* Transformed Request */}
              {transformedRequest && (
                <CollapsibleJsonSection
                  title="Transformed Request"
                  content={transformedRequest}
                  icon="🔄"
                />
              )}

              {/* Full Response */}
              {result && (
                <CollapsibleJsonSection
                  title="Full Response"
                  content={result}
                  icon="📋"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!hasResult && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <p className="text-sm">Click "Run Classification Pipeline" to execute the test</p>
        </div>
      )}
    </div>
  );
}

interface CollapsibleJsonSectionProps {
  title: string;
  content: any;
  icon: string;
}

function CollapsibleJsonSection({ title, content, icon }: CollapsibleJsonSectionProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
      >
        <span className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
          <span>{icon}</span>
          {title}
        </span>
        <span className={`text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>

      {isOpen && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-900">
          <pre className="text-xs font-mono text-gray-900 dark:text-gray-100 overflow-x-auto max-h-96 overflow-y-auto">
            {JSON.stringify(content, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
